import { useEffect } from 'react';

interface CityHeroProps {
  cityName: string;
}

export default function CityHero({ cityName }: CityHeroProps) {
  useEffect(() => {
    // Preload critical images
    const preloadImages = [
      'https://public.readdy.ai/ai/img_res/f58ae8c2489dc716118d4fe0133fed61.jpg',
      'https://readdy.ai/api/search-image?query=confident%20professional%20window%20repair%20technician%20expert%20craftsman%20in%20clean%20modern%20work%20uniform%20holding%20professional%20tools%20smiling%20warmly%20with%20expertise%20and%20trustworthiness%20simple%20clean%20background%20showcasing%20professionalism%20and%20reliability&width=400&height=500&seq=hero-expert-city-002&orientation=portrait'
    ];

    preloadImages.forEach((src) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20professional%20window%20repair%20service%20expert%20working%20on%20elegant%20contemporary%20residential%20windows%20with%20professional%20tools%20in%20bright%20clean%20workspace%20showcasing%20craftsmanship%20quality%20service%20and%20attention%20to%20detail%20with%20soft%20natural%20lighting&width=1920&height=1080&seq=hero-bg-city-001&orientation=landscape"
          alt={`Expert en réparation de fenêtres ${cityName}`}
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-700/85"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Desktop Layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Image */}
          <div className="relative animate-fade-in-left">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-lift">
              <img
                src="https://readdy.ai/api/search-image?query=confident%20professional%20window%20repair%20technician%20expert%20craftsman%20in%20clean%20modern%20work%20uniform%20holding%20professional%20tools%20smiling%20warmly%20with%20expertise%20and%20trustworthiness%20simple%20clean%20background%20showcasing%20professionalism%20and%20reliability&width=400&height=500&seq=hero-expert-city-002&orientation=portrait"
                alt={`Expert réparateur de fenêtres à ${cityName}`}
                className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 animate-float hover-scale cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">15+</p>
                  <p className="text-sm text-gray-600">Ans d'expérience</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="animate-fade-in-right">
            <div className="inline-block bg-amber-400 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-scale-in hover-scale cursor-pointer">
              <i className="ri-star-fill mr-2"></i>
              Service réparation fenêtres – {cityName}
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up delay-100">
              Réparation de Fenêtres à {cityName}
            </h1>
            
            <p className="text-xl text-blue-100 mb-8 leading-relaxed animate-fade-in-up delay-200">
              Expert en <a href="/reparation-fenetre-pvc-lyon" className="text-amber-400 hover:text-amber-300 underline font-medium transition-colors">réparation</a> et entretien de fenêtres PVC, aluminium et bois. 
              Intervention rapide à {cityName} et dans toute l'agglomération lyonnaise.
            </p>

            {/* Trust Points */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 animate-fade-in-up delay-300 hover:translate-x-2 transition-transform cursor-pointer">
                <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <p className="text-white font-medium">Devis gratuit sous 24h</p>
              </div>
              <div className="flex items-center gap-3 animate-fade-in-up delay-400 hover:translate-x-2 transition-transform cursor-pointer">
                <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <p className="text-white font-medium">Intervention rapide 7j/7</p>
              </div>
              <div className="flex items-center gap-3 animate-fade-in-up delay-500 hover:translate-x-2 transition-transform cursor-pointer">
                <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-lg text-gray-900"></i>
                </div>
                <p className="text-white font-medium">Garantie satisfaction 100%</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-600">
              <a
                href="#contact"
                className="bg-amber-400 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-amber-500 transition-all duration-300 shadow-lg hover:shadow-xl text-center whitespace-nowrap cursor-pointer btn-ripple hover-lift"
              >
                <i className="ri-phone-line mr-2"></i>
                Devis Gratuit
              </a>
              <a
                href="tel:0980803684"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/30 text-center whitespace-nowrap cursor-pointer hover-lift"
              >
                <i className="ri-customer-service-2-line mr-2"></i>
                09 80 80 36 84
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Layout - OPTIMIZED FOR CONVERSION */}
        <div className="md:hidden text-center px-2">
          <div className="inline-block bg-amber-400 text-gray-900 px-5 py-2.5 rounded-full text-sm font-bold mb-6 animate-scale-in shadow-lg">
            <i className="ri-star-fill mr-2"></i>
            Expert {cityName}
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-5 leading-tight animate-fade-in-up delay-100 px-2">
            Réparation de Fenêtres à {cityName}
          </h1>
          
          <p className="text-base sm:text-lg text-blue-50 mb-8 leading-relaxed max-w-lg mx-auto animate-fade-in-up delay-200 px-2">
            Expert en réparation de fenêtres PVC, alu et bois. Intervention rapide 7j/7.
          </p>

          {/* CTA Buttons Mobile - PRIORITÉ MAXIMALE */}
          <div className="flex flex-col gap-3 max-w-sm mx-auto mb-8 animate-fade-in-up delay-300 px-2">
            <a
              href="tel:0980803684"
              className="bg-amber-400 text-gray-900 px-6 py-5 rounded-xl font-bold text-lg hover:bg-amber-500 transition-all duration-300 shadow-2xl text-center w-full cursor-pointer flex items-center justify-center gap-3 transform hover:scale-105 active:scale-95"
            >
              <i className="ri-phone-fill text-2xl"></i>
              <div className="text-left">
                <div className="text-xs font-medium opacity-80">Appelez maintenant</div>
                <div className="text-xl font-extrabold">09 80 80 36 84</div>
              </div>
            </a>
            <a
              href="#contact"
              className="bg-white text-teal-700 px-6 py-4 rounded-xl font-bold text-base hover:bg-gray-50 transition-all duration-300 shadow-xl text-center w-full cursor-pointer flex items-center justify-center gap-2 border-2 border-white"
            >
              <i className="ri-mail-send-line text-xl"></i>
              Devis Gratuit par Email
            </a>
          </div>

          {/* Trust Points Mobile - COMPACT */}
          <div className="space-y-3 mb-6 max-w-xs mx-auto">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 animate-fade-in-up delay-400">
              <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-lg text-gray-900"></i>
              </div>
              <p className="text-white font-semibold text-sm text-left">Devis sous 24h</p>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 animate-fade-in-up delay-500">
              <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-lg text-gray-900"></i>
              </div>
              <p className="text-white font-semibold text-sm text-left">Dispo 7j/7</p>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 animate-fade-in-up delay-600">
              <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-lg text-gray-900"></i>
              </div>
              <p className="text-white font-semibold text-sm text-left">Garantie 100%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <a href="#services" className="block text-white cursor-pointer hover:text-amber-400 transition-colors">
          <i className="ri-arrow-down-line text-3xl"></i>
        </a>
      </div>
    </section>
  );
}
